/**
 * POL-644 — Brand theme application for the widget Shadow DOM.
 *
 * The widget's base stylesheet (POL-582) declares a contract of
 * `--polst-*` CSS custom properties on `:host`. This module reads the
 * resolved brand theme returned by the public REST DTO (POL-642) and
 * writes those values onto the host element as inline style overrides
 * — inline styles beat the `:host {}` defaults, so the brand wins
 * without any !important hacks.
 *
 * Precedence order (lowest → highest):
 *   1. `theme.ts` defaults inside the shadow stylesheet
 *   2. Brand theme — applied here as inline `style="--polst-*: …"` on
 *      the host element
 *   3. Host-page overrides — `data-theme="dark"` / `data-accent="#…"`
 *      on the widget element. The data-theme attribute already beats
 *      brand because the dark/light selectors live OUTSIDE the inline
 *      style cascade. `data-accent` is re-applied as inline style by
 *      `mount()` so it stomps the brand value.
 *
 * Side surfaces handled here:
 *   - Optional `@font-face` for `fontStack === "BRAND"` injected as a
 *     `<style>` INSIDE the shadow root (never `document.head`).
 *   - Sanitised `customCss` appended as the final stylesheet inside
 *     the shadow root.
 *   - Watermark gating: pure data flow — the widget reads
 *     `theme.hideWatermark` verbatim because the backend has already
 *     gated it on the brand's `embed.hideWatermark` entitlement
 *     (POL-650). No client-side premium check.
 *   - `prefers-color-scheme` watcher used by logo / watermark consumers
 *     to swap their light/dark variants on system theme flips.
 *
 * Zero runtime deps; jsdom-friendly; every helper is pure and exported
 * so unit tests can probe each rule in isolation.
 */

import {
  BRAND_DENSITY_SCALES,
  BRAND_FONT_STACKS,
  BRAND_RADIUS_PX,
  type ThemeMode,
} from "./theme";

// ─── DTO type ───────────────────────────────────────────────────────────────

/**
 * Local mirror of the resolved brand-theme DTO (POL-642). Hand-written
 * so the widget package keeps zero deps on the backend type tree. The
 * shape is verified against the canonical schema by the parity test.
 *
 * Every field is optional — the resolver always returns the full
 * envelope, but consumers may pass partial objects (and unset fields
 * fall back to `:host {}` defaults).
 */
export interface BrandThemeDTO {
  accent?: string | null;
  background?: string | null;
  foreground?: string | null;
  muted?: string | null;
  border?: string | null;
  buttonBg?: string | null;
  buttonFg?: string | null;
  buttonBgHover?: string | null;
  success?: string | null;
  danger?: string | null;
  radius?: "NONE" | "SM" | "MD" | "LG" | "FULL";
  density?: "COMPACT" | "COMFORTABLE";
  fontStack?: "SYSTEM" | "SERIF" | "MONO" | "BRAND";
  fontBrandUrl?: string | null;
  logoLightUrl?: string | null;
  logoDarkUrl?: string | null;
  faviconUrl?: string | null;
  hideWatermark?: boolean;
  customCss?: string | null;
  version?: number;
}

/**
 * Hex-colour validator. Mirrors the backend regex (POL-642) so we never
 * write a malformed value to inline styles. Both `#rrggbb` and
 * `#rrggbbaa` are accepted; lower or upper case.
 */
const COLOUR_REGEX = /^#([0-9a-f]{6}|[0-9a-f]{8})$/i;

/** Maximum customCss byte length, mirrored from backend. */
export const BRAND_CUSTOM_CSS_MAX_BYTES = 4 * 1024;

// ─── CSS-var mapping ───────────────────────────────────────────────────────

/**
 * Translate a resolved brand theme into the `Record<varName, value>`
 * map that should be written onto the host element as inline style.
 *
 * Null / undefined colours are SKIPPED so the `:host {}` defaults
 * shine through. Enums always map to a concrete value (radius, density,
 * fontStack always have a Prisma default on the row).
 */
export function mapBrandThemeToCssVars(theme: BrandThemeDTO): Record<string, string> {
  const out: Record<string, string> = {};

  // Colours — skip nulls.
  const setColour = (name: string, value: string | null | undefined) => {
    if (typeof value !== "string") return;
    if (!COLOUR_REGEX.test(value)) return;
    out[name] = value;
  };
  setColour("--polst-accent", theme.accent);
  setColour("--polst-bg", theme.background);
  setColour("--polst-fg", theme.foreground);
  setColour("--polst-muted-fg", theme.muted);
  setColour("--polst-accent-fg", theme.buttonFg);
  setColour("--polst-button-hover", theme.buttonBgHover);
  setColour("--polst-error-fg", theme.danger);

  // Radius enum → px scale.
  if (theme.radius && theme.radius in BRAND_RADIUS_PX) {
    out["--polst-radius"] = BRAND_RADIUS_PX[theme.radius];
  }

  // Density enum → spacing scale.
  if (theme.density && theme.density in BRAND_DENSITY_SCALES) {
    const scale = BRAND_DENSITY_SCALES[theme.density];
    out["--polst-spacing-xs"] = scale.xs;
    out["--polst-spacing-sm"] = scale.sm;
    out["--polst-spacing-md"] = scale.md;
    out["--polst-spacing-lg"] = scale.lg;
    out["--polst-spacing-xl"] = scale.xl;
  }

  // Font stack enum → CSS font stack. BRAND uses the brand-injected
  // family followed by the SYSTEM stack as a fallback so the widget
  // remains legible while the brand font is downloading (or if it 404s).
  if (theme.fontStack && theme.fontStack in BRAND_FONT_STACKS) {
    out["--polst-font-stack"] = BRAND_FONT_STACKS[theme.fontStack];
  }

  return out;
}

// ─── customCss sanitiser ───────────────────────────────────────────────────

/**
 * Sanitise a brand-supplied stylesheet so it cannot:
 *   - Pull in remote stylesheets via `@import`.
 *   - Execute JS via `url(javascript:…)`.
 *   - Reference URLs whose scheme is not `data:` or `https:`.
 *   - Break the widget out of the host page via `position: fixed` on
 *     `:host` (which would let the brand take over the viewport).
 *
 * The implementation is intentionally conservative — when in doubt we
 * drop the rule rather than try to repair it. The function returns
 * `""` on bad input (null/undefined/empty) so callers can blindly
 * concatenate the result.
 *
 * Size cap mirrors `BRAND_CUSTOM_CSS_MAX_BYTES`. A string longer than
 * that is rejected wholesale (returns `""`) — the backend already
 * enforces the same cap on PATCH, so reaching this branch implies a
 * tampered DTO.
 */
export function sanitizeBrandCustomCss(css: string | null | undefined): string {
  if (typeof css !== "string") return "";
  if (css.length > BRAND_CUSTOM_CSS_MAX_BYTES) return "";

  let out = css;

  // Strip `@import` rules entirely (line + statement variants).
  // Both `@import "…";` and `@import url(…);` shapes are caught by the
  // leading directive match.
  out = out.replace(/@import[^;]*;?/gi, "");

  // Strip every `url(scheme:…)` whose scheme is not allowed. Allowed
  // schemes: `data:` and `https:`. Bare paths (`url("foo.png")`) are
  // also kept — they resolve relative to the embed page, which is
  // tolerable.
  out = out.replace(/url\(\s*(['"]?)([^)'"\s]+)\1\s*\)/gi, (match, _q, raw) => {
    const value = String(raw).trim();
    // Bare path — no colon before the first slash → relative URL.
    if (!/^[a-z][a-z0-9+\-.]*:/i.test(value)) return match;
    if (/^https:/i.test(value)) return match;
    if (/^data:/i.test(value)) return match;
    // Anything else (javascript:, http:, file:, vbscript:, …) → drop.
    return "url()";
  });

  // Block the brand from breaking out of the embed via `position: fixed`
  // (or sticky, which has the same escape vector once promoted) targeted
  // at `:host`. We ONLY block the `:host` selector — brand-supplied
  // styles inside the widget can use `position: fixed` on their own
  // descendants if they really want to (rare, but legitimate for tooltips).
  out = out.replace(
    /:host[^{]*\{[^}]*position\s*:\s*(fixed|sticky)[^}]*\}/gi,
    "",
  );

  return out;
}

// ─── Logo resolution ───────────────────────────────────────────────────────

/**
 * Pick the logo URL appropriate for the current colour scheme.
 *   - `light` → prefer `logoLightUrl`, fall back to `logoDarkUrl`.
 *   - `dark`  → prefer `logoDarkUrl`, fall back to `logoLightUrl`.
 * Returns `null` when the brand has no logo configured at all.
 */
export function resolveLogoUrl(
  theme: BrandThemeDTO,
  scheme: "light" | "dark",
): string | null {
  const light = theme.logoLightUrl ?? null;
  const dark = theme.logoDarkUrl ?? null;
  if (scheme === "dark") return dark ?? light ?? null;
  return light ?? dark ?? null;
}

// ─── Colour-scheme watcher ─────────────────────────────────────────────────

/**
 * Resolve the effective colour scheme for a host element, taking the
 * forced `data-theme` override into account first.
 */
export function effectiveColorScheme(host: Element | null | undefined): "light" | "dark" {
  if (host && typeof (host as Element).getAttribute === "function") {
    const forced = (host as Element).getAttribute("data-theme");
    if (forced === "dark") return "dark";
    if (forced === "light") return "light";
  }
  // Defer to the OS via matchMedia. SSR / jsdom-without-matchMedia
  // returns "light" by default.
  if (typeof globalThis !== "undefined" && typeof globalThis.matchMedia === "function") {
    try {
      const mql = globalThis.matchMedia("(prefers-color-scheme: dark)");
      if (mql && mql.matches) return "dark";
    } catch {
      /* ignore */
    }
  }
  return "light";
}

/**
 * Subscribe to `prefers-color-scheme` changes for the duration of the
 * widget's lifetime. Returns a teardown that detaches the listener.
 *
 * The host's `data-theme` attribute is consulted on every fire so a
 * forced theme suppresses the callback (the consumer would not want to
 * swap a logo when the user has explicitly pinned the palette).
 *
 * Robust against:
 *   - SSR (no `matchMedia` global) → returns a no-op teardown.
 *   - Stale listener APIs (Safari < 14 used `addListener`/`removeListener`
 *     instead of `addEventListener`/`removeEventListener`) — both are
 *     attached when present so we work everywhere.
 */
export function watchColorScheme(
  host: Element | null,
  callback: (scheme: "light" | "dark") => void,
): () => void {
  if (typeof globalThis === "undefined" || typeof globalThis.matchMedia !== "function") {
    return () => {};
  }
  let mql: MediaQueryList;
  try {
    mql = globalThis.matchMedia("(prefers-color-scheme: dark)");
  } catch {
    return () => {};
  }
  const handler = () => {
    const scheme = effectiveColorScheme(host);
    callback(scheme);
  };
  // Modern API.
  if (typeof mql.addEventListener === "function") {
    mql.addEventListener("change", handler);
  }
  // Legacy API (Safari ≤ 13).
  const legacy = mql as unknown as {
    addListener?: (cb: (e: MediaQueryListEvent) => void) => void;
    removeListener?: (cb: (e: MediaQueryListEvent) => void) => void;
  };
  if (typeof legacy.addListener === "function") {
    legacy.addListener(handler);
  }
  return () => {
    if (typeof mql.removeEventListener === "function") {
      mql.removeEventListener("change", handler);
    }
    if (typeof legacy.removeListener === "function") {
      legacy.removeListener(handler);
    }
  };
}

// ─── Application ───────────────────────────────────────────────────────────

const BRAND_FONT_FACE_FAMILY = "PolstBrand";
const BRAND_FONT_STYLE_MARKER = "data-polst-brand-font";
const BRAND_CUSTOM_CSS_STYLE_MARKER = "data-polst-brand-custom-css";

/**
 * Validate the brand `fontBrandUrl`. Only `https:` is allowed —
 * `data:` URIs would let a brand smuggle arbitrary MIME types into
 * the font request, and `http:` is unsafe in mixed-content contexts.
 */
function isValidFontUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Apply a brand theme to a freshly-created shadow root.
 *
 * Sequence:
 *   1. Write the colour / radius / spacing / font-stack vars onto the
 *      host element as inline style. These beat the `:host {}` defaults
 *      that the base stylesheet declares.
 *   2. Inject the brand `@font-face` (when `fontStack === "BRAND"` and
 *      a valid https URL is configured) into the shadow root.
 *   3. Append the sanitised `customCss` as the LAST stylesheet inside
 *      the shadow root so brand selectors override every base rule.
 *
 * Returns a teardown that reverses step (1)'s style mutations and
 * removes the injected `<style>` elements. Idempotent when called
 * multiple times — each call replaces the previous brand styles.
 *
 * Caller is expected to pass the same `host` that was passed to
 * `createRoot()`. The function does NOT touch `data-theme` /
 * `data-accent` host overrides — those are owned by `mount()` so the
 * precedence chain (defaults < brand < host overrides) stays intact.
 */
export function applyBrandTheme(
  host: Element,
  root: ShadowRoot,
  theme: BrandThemeDTO | null | undefined,
  options: { themeMode?: ThemeMode } = {},
): () => void {
  if (!host || typeof (host as HTMLElement).style === "undefined") {
    throw new Error("applyBrandTheme(): host must be an HTMLElement");
  }
  if (!theme) {
    return () => {};
  }

  const styleEl = host as HTMLElement;
  const previousProperties: Record<string, string> = {};
  const vars = mapBrandThemeToCssVars(theme);
  for (const [name, value] of Object.entries(vars)) {
    // Capture the prior inline value so teardown can restore (typically
    // empty string, since the host had nothing on it before).
    previousProperties[name] = styleEl.style.getPropertyValue(name);
    styleEl.style.setProperty(name, value);
  }

  // Brand @font-face injection. We only emit when both the stack is
  // BRAND and the URL is a valid https URL — anything else is silently
  // skipped (the SYSTEM fallback is in the family list anyway).
  let brandFontEl: HTMLStyleElement | null = null;
  // Drop any pre-existing brand font element from a previous apply.
  const previousFont = root.querySelector(`style[${BRAND_FONT_STYLE_MARKER}]`);
  if (previousFont && previousFont.parentNode) {
    previousFont.parentNode.removeChild(previousFont);
  }
  if (
    theme.fontStack === "BRAND" &&
    typeof theme.fontBrandUrl === "string" &&
    isValidFontUrl(theme.fontBrandUrl)
  ) {
    const doc = (root as unknown as { ownerDocument?: Document }).ownerDocument;
    if (doc) {
      brandFontEl = doc.createElement("style");
      brandFontEl.setAttribute(BRAND_FONT_STYLE_MARKER, "");
      // Quote the URL with single quotes — the URL is already validated
      // as a parsed URL so we don't need to escape further.
      brandFontEl.textContent = `@font-face {
  font-family: "${BRAND_FONT_FACE_FAMILY}";
  src: url('${theme.fontBrandUrl}') format('woff2'), url('${theme.fontBrandUrl}');
  font-display: swap;
}
:host {
  --polst-font-stack: "${BRAND_FONT_FACE_FAMILY}", ${BRAND_FONT_STACKS.SYSTEM};
}`;
      root.appendChild(brandFontEl);
      // Override the previous --polst-font-stack inline value so the
      // brand family takes precedence over the SYSTEM fallback already
      // written above.
      styleEl.style.setProperty(
        "--polst-font-stack",
        `"${BRAND_FONT_FACE_FAMILY}", ${BRAND_FONT_STACKS.SYSTEM}`,
      );
    }
  }

  // customCss injection — sanitise then append as the LAST stylesheet
  // inside the shadow root so brand selectors win.
  let customCssEl: HTMLStyleElement | null = null;
  // Drop any pre-existing brand customCss element.
  const previousCustom = root.querySelector(`style[${BRAND_CUSTOM_CSS_STYLE_MARKER}]`);
  if (previousCustom && previousCustom.parentNode) {
    previousCustom.parentNode.removeChild(previousCustom);
  }
  const sanitised = sanitizeBrandCustomCss(theme.customCss);
  if (sanitised) {
    const doc = (root as unknown as { ownerDocument?: Document }).ownerDocument;
    if (doc) {
      customCssEl = doc.createElement("style");
      customCssEl.setAttribute(BRAND_CUSTOM_CSS_STYLE_MARKER, "");
      customCssEl.textContent = sanitised;
      root.appendChild(customCssEl);
    }
  }

  // `themeMode` is not consumed directly here today, but we accept it
  // so future calls can short-circuit work that doesn't apply when the
  // user has forced light/dark. The argument keeps the signature
  // future-proof.
  void options.themeMode;

  return () => {
    // Restore prior inline values. Setting an empty string removes the
    // property cleanly; preserving the prior value keeps the chain
    // working when an external caller stacks brand themes.
    for (const [name, prev] of Object.entries(previousProperties)) {
      if (prev) {
        styleEl.style.setProperty(name, prev);
      } else {
        styleEl.style.removeProperty(name);
      }
    }
    if (brandFontEl && brandFontEl.parentNode) {
      brandFontEl.parentNode.removeChild(brandFontEl);
    }
    if (customCssEl && customCssEl.parentNode) {
      customCssEl.parentNode.removeChild(customCssEl);
    }
  };
}

// ─── Host overrides ────────────────────────────────────────────────────────

/**
 * Re-apply the host element's `data-accent` attribute as an inline
 * `--polst-accent` so it stomps any brand-supplied accent. Returns a
 * teardown that removes ONLY the override — the brand colour underneath
 * is untouched (its own teardown still works).
 *
 * `data-theme="dark"` / `light"` doesn't need a re-apply because the
 * theme stylesheet's `:host([data-theme="dark"])` selector already
 * wins over the inline `--polst-bg` etc. (selector specificity ties,
 * but the `[data-theme]` selector applies later in the cascade).
 */
export function applyHostAccentOverride(host: Element): () => void {
  if (!host || typeof (host as HTMLElement).style === "undefined") return () => {};
  const accent = host.getAttribute("data-accent");
  if (!accent) return () => {};
  const normalised = accent.startsWith("#") ? accent : `#${accent}`;
  if (!COLOUR_REGEX.test(normalised)) return () => {};
  const styleEl = host as HTMLElement;
  const prev = styleEl.style.getPropertyValue("--polst-accent");
  styleEl.style.setProperty("--polst-accent", normalised);
  return () => {
    if (prev) styleEl.style.setProperty("--polst-accent", prev);
    else styleEl.style.removeProperty("--polst-accent");
  };
}
